<template>
  <div class="leaderboard">
    <div v-if="isLoading" class="loading-container">
      <b-loading :is-full-page="false" v-model="isLoading"></b-loading>
      <p class="has-text-centered">Loading class leaderboard...</p>
    </div>

    <div v-else-if="error" class="error-container">
      <div class="notification is-danger">
        <p><strong>Error loading leaderboard:</strong></p>
        <p>{{ error }}</p>
        <b-button
          @click="loadClassLeaderboard"
          type="is-primary"
          size="is-small"
        >
          Retry
        </b-button>
      </div>
    </div>

    <div v-else-if="cumulativeScores.length === 0" class="empty-container">
      <div class="notification is-info">
        <p>No student data available for this class.</p>
        <p v-if="lectures.length === 0">No lectures found for this class.</p>
        <p v-else>
          {{ lectures.length }} lecture(s) found, but no student data.
        </p>
      </div>
    </div>

    <div v-else class="student-list">
      <div
        v-for="(item, index) in leaderboardItems"
        :key="item.student.studentId"
        class="student-row"
      >
        <div class="rank-cell">
          <span class="rank-number">{{ index + 1 }}</span>
        </div>
        <div class="medal-cell">
          <img
            v-if="item.medal"
            :src="item.medal.image"
            :alt="item.medal.alt"
            :class="['medal-image', item.medal.class]"
          />
        </div>
        <div class="student-name">
          {{ item.student.firstName }} {{ item.student.lastName }}
        </div>
        <div class="score-cell">
          <ClassLeaderboardScore :cumulativeScore="item.student" />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import { ClassRollLeaderboardCalculator } from "vue-chess-shared/calculators/ClassRollLeaderboardCalculator";
import ClassLeaderboardScore from "@/components/shared/ClassLeaderboardScore.vue";
import { chessPieceSvg } from "@/utils/chess-svg";
import SACFrontendService from "@/services/SACFrontendService";

const MEDALS = [
  {
    image: require("@/assets/img/leaderboard/gold-medal.svg"),
    alt: "Gold Medal",
    class: "",
  },
  {
    image: require("@/assets/img/leaderboard/silver-medal.svg"),
    alt: "Silver Medal",
    class: "",
  },
  {
    image: require("@/assets/img/leaderboard/bronze-medal.svg"),
    alt: "Bronze Medal",
    class: "",
  },
  {
    image: chessPieceSvg("KING_WHITE"),
    alt: "King",
    class: "king-icon",
  },
  {
    image: chessPieceSvg("QUEEN_WHITE"),
    alt: "Queen",
    class: "queen-icon",
  },
  {
    image: chessPieceSvg("ROOK_WHITE"),
    alt: "Rook",
    class: "rook-icon",
  },
  {
    image: chessPieceSvg("BISHOP_WHITE"),
    alt: "Bishop",
    class: "bishop-icon",
  },
  {
    image: chessPieceSvg("KNIGHT_WHITE"),
    alt: "Knight",
    class: "knight-icon",
  },
  {
    image: chessPieceSvg("PAWN_WHITE"),
    alt: "Pawn",
    class: "pawn-icon",
  },
];

export default {
  name: "ClassLeaderboard",

  components: {
    ClassLeaderboardScore,
  },

  data() {
    return {
      isLoading: false,
      cumulativeScores: [],
      lectures: [],
      error: null,
    };
  },

  computed: {
    ...mapGetters({
      currentClass: "getCurrentClass",
    }),

    students() {
      return this.currentClass?.students || [];
    },

    medals() {
      const totalStudents = this.cumulativeScores.length;
      const maxMedals = totalStudents <= 10 ? 3 : totalStudents <= 20 ? 6 : 9;

      return MEDALS.slice(0, maxMedals);
    },

    leaderboardItems() {
      return this.cumulativeScores.map((cumulativeScore, index) => ({
        student: {
          studentId: cumulativeScore.studentId,
          firstName: cumulativeScore.firstName,
          lastName: cumulativeScore.lastName,
          // Create a synthetic student object for the score component
          // that represents the cumulative totals
          cumulativeTotal: cumulativeScore.cumulativeTotal,
          lectureBreakdowns: cumulativeScore.lectureBreakdowns,
        },
        score: cumulativeScore.cumulativeTotal,
        rank: index + 1,
        medal: this.medals[index] || null,
      }));
    },
  },

  async created() {
    await this.loadClassLeaderboard();
  },

  watch: {
    currentClass: {
      handler() {
        if (this.currentClass?.classId) {
          this.loadClassLeaderboard();
        }
      },
      immediate: true,
    },
  },

  methods: {
    async loadClassLeaderboard() {
      if (!this.currentClass?.classId) {
        return;
      }

      this.isLoading = true;
      this.error = null;

      try {
        // Step 1: Get all lectures for this class
        const lectures = await SACFrontendService.getLecturesByClassId(
          this.currentClass.classId
        );
        this.lectures = lectures;

        if (lectures.length === 0) {
          this.cumulativeScores = [];
          return;
        }

        // Step 2: For each lecture, get student performance data
        const lectureStudents = [];

        for (const lecture of lectures) {
          try {
            const lectureDetails = await SACFrontendService.getClassDetails(
              lecture.year,
              parseInt(lecture.term),
              lecture.classId,
              lecture.cdid
            );

            // Process each student's data for this lecture
            lectureDetails.students.forEach((student) => {
              lectureStudents.push({
                lectureId: lecture.cdid,
                date: lecture.date,
                studentId: student.studentId,
                firstName: student.firstName,
                lastName: student.lastName,
                performanceInput: {
                  awards: student.awards || [],
                  behavior: student.behavior,
                  tournamentWins: student.tournamentWins || 0,
                  tournamentDraws: student.tournamentDraws || 0,
                  tournamentLosses: student.tournamentLosses || 0,
                },
              });
            });
          } catch (lectureError) {
            console.warn(
              `Failed to load data for lecture ${lecture.cdid}:`,
              lectureError
            );
            // Continue with other lectures even if one fails
          }
        }

        // Step 3: Calculate cumulative scores
        const cumulativeScores =
          ClassRollLeaderboardCalculator.calculateStudentScoreBreakdowns(
            lectureStudents
          );

        this.cumulativeScores = cumulativeScores;
      } catch (error) {
        console.error("Failed to load class leaderboard:", error);
        this.error = error.message || "Failed to load leaderboard data";
        this.cumulativeScores = [];
      } finally {
        this.isLoading = false;
      }
    },
  },
};
</script>

<style scoped>
.leaderboard {
  display: flex;
  flex-direction: column;
  min-height: 400px;
  max-height: 100%;
}

.student-list {
  background: white;
  flex: 1;
  overflow-y: auto;
  min-height: 0;
  max-height: 100%;
}

.student-row {
  display: flex;
  align-items: center;
  padding: 1rem 1.5rem;
  border-bottom: 1px solid #f0f0f0;
}

.student-row:hover {
  background-color: #f8f9fa;
}

.rank-cell {
  width: 50px;
  text-align: center;
  padding: 0 0.5rem;
  flex-shrink: 0;
}

.rank-number {
  font-size: 1.2rem;
  font-weight: 700;
  color: #333;
}

.medal-cell {
  width: 60px;
  text-align: center;
  padding: 0 0.5rem;
  /* Ensure consistent height */
  height: 48px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.medal-image {
  width: 48px;
  height: 48px;
  object-fit: contain;
  /* Ensure consistent row height */
  max-height: 48px;
  vertical-align: middle;
}

/* Chess piece hierarchy - smaller than medals, progressive size reduction */
.medal-image.king-icon {
  width: 42px !important;
  height: 42px !important;
  max-width: 42px !important;
  max-height: 42px !important;
}

.medal-image.queen-icon {
  width: 38px !important;
  height: 38px !important;
  max-width: 38px !important;
  max-height: 38px !important;
}

.medal-image.rook-icon {
  width: 34px !important;
  height: 34px !important;
  max-width: 34px !important;
  max-height: 34px !important;
}

.medal-image.bishop-icon {
  width: 30px !important;
  height: 30px !important;
  max-width: 30px !important;
  max-height: 30px !important;
}

.medal-image.knight-icon {
  width: 26px !important;
  height: 26px !important;
  max-width: 26px !important;
  max-height: 26px !important;
}

.medal-image.pawn-icon {
  width: 22px !important;
  height: 22px !important;
  max-width: 22px !important;
  max-height: 22px !important;
}

.student-name {
  flex: 1;
  font-weight: 600;
  font-size: 1.2rem;
  color: #212529;
  padding: 0 1rem;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
}

.score-cell {
  width: 120px;
  text-align: right;
  font-weight: 600;
}

.loading-container {
  position: relative;
  min-height: 200px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.error-container,
.empty-container {
  padding: 2rem;
}

.error-container .notification,
.empty-container .notification {
  margin: 0;
}
</style>
